import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Menu as MenuIcon, ShoppingBag, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';

import { announcementStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import type { StorefrontMenuItem } from '@/features/storefront/api/storefront';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import {
  useCategoryPath,
  useShelfCategoryTree,
  type ShelfBranch,
} from '@/features/storefront/hooks/useShelfCategories';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import { useLanguageSwitcherVisible } from '@/features/storefront/i18n/switcher';
import { useStorefrontPaths } from '@/features/storefront/paths';
import {
  CategoryMegaPanel,
  megaMenuStyle,
  useHoverDisclosure,
} from '@/features/storefront/layouts/chrome/MegaMenu';
import { CategoryLinkLabel } from '@/features/storefront/layouts/chrome/CategoryLinkIcon';
import { useChromeOverlay } from '@/features/storefront/layouts/chrome/previewEditing';
import { CHROME_QUIET_SCROLL, headerCategoryLimit } from '@/features/storefront/layouts/chrome/types';
import { useFittingCount } from '@/features/storefront/layouts/chrome/useFittingCount';
import { safeHref } from '@/features/storefront/sections/schema';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_MONO_STYLE } from '@/features/storefront/theme';
import type { HeaderBanner, StorefrontTheme } from '@/features/storefront/themeSchema';
import { cn } from '@/lib/cn';
import { fixedImageSizes } from '@/lib/imageRenditions';

/**
 * The pieces every header and footer variant is assembled from. Shared here so
 * a family changes the arrangement, never the behaviour: external links, the
 * logo's accessibility rules and the footer fallbacks work identically in all
 * four looks.
 */

/**
 * Links are stored as the merchant typed them, so an off-site one has to leave
 * the router alone - `<Link to="https://...">` would try to route to it.
 *
 * In `preview` the link is an inert span: the customizer shows the merchant
 * their navigation without letting a stray click leave the builder.
 */
export function MenuLink({
  item,
  className,
  preview,
}: {
  item: StorefrontMenuItem;
  className?: string;
  preview?: boolean;
}) {
  const paths = useStorefrontPaths();

  if (preview) {
    return <span className={className}>{item.label}</span>;
  }

  const external = /^(https?:)?\/\//.test(item.url) || item.url.startsWith('mailto:');
  if (external) {
    return (
      <a href={item.url} className={className} rel="noreferrer">
        {item.label}
      </a>
    );
  }
  // Storefront paths are written the way a shopper sees them ("/cart"). Which
  // shop they are read against - the merchant's, or a theme's preview - is
  // what `paths` carries.
  return (
    <Link to={paths.menu(item.url)} className={className}>
      {item.label}
    </Link>
  );
}

/**
 * The masthead's own button - "Book appointment", "Get a quote" - at the very
 * end of the header, after the shopper's corner.
 *
 * One component for every layout: `StorefrontHeader` appends it to the
 * `endSlot` it hands whichever masthead the shop wears, so a family never
 * has to know it exists and a new family gets it for free. Drawn only with
 * both a label and a link that passes `safeHref` - half a button is no
 * button, and a header saved before this existed has neither.
 *
 * Painted like every primary button in the shop (`STOREFRONT_BUTTON_STYLE`),
 * smaller on a phone and cut short there, because the corner it stands in is
 * already holding the search, the account and the basket. In `preview` it is
 * an inert span, like every other link in the customiser's header.
 */
export function HeaderCta({
  header,
  preview,
}: {
  header: Pick<StorefrontTheme['header'], 'cta_label' | 'cta_href'>;
  preview?: boolean;
}) {
  const paths = useStorefrontPaths();
  const label = header.cta_label?.trim() ?? '';
  const href = safeHref(header.cta_href);
  if (!label || !href) return null;

  const className =
    'inline-flex h-8 max-w-[8.5rem] shrink-0 items-center px-3 text-xs font-semibold sm:h-9 sm:max-w-none sm:px-4 sm:text-sm';
  const inner = <span className="truncate">{label}</span>;

  if (preview) {
    return (
      <span data-header-cta="" className={className} style={STOREFRONT_BUTTON_STYLE}>
        {inner}
      </span>
    );
  }
  // Off-site, mail and phone links, and anchors, leave the router alone; a
  // shop path is read against whichever shop this is (`paths.menu`).
  if (/^(https?:)?\/\//i.test(href) || /^(mailto|tel):/i.test(href) || href.startsWith('#')) {
    return (
      <a data-header-cta="" href={href} className={className} style={STOREFRONT_BUTTON_STYLE} rel="noreferrer">
        {inner}
      </a>
    );
  }
  return (
    <Link data-header-cta="" to={paths.menu(href)} className={className} style={STOREFRONT_BUTTON_STYLE}>
      {inner}
    </Link>
  );
}

/**
 * The announcement rail above the masthead: the merchant's standing promises,
 * one at a time.
 *
 * A shop has more than one thing to say - free shipping, the sale that ends
 * Sunday, the number to call - and a header has room for one line, so the rail
 * rotates rather than stacking. Rotation is a convenience, never the only way
 * through: the dots move it by hand, hovering or tabbing into it holds it
 * still, and turning autoplay off leaves the dots doing all the work.
 *
 * Renders nothing when the merchant has written no banners and the family
 * passes no `fallback`, so a header that never had a rail does not grow one.
 */
export function HeaderBannerRail({
  header,
  fallback,
  preview,
}: {
  header: StorefrontTheme['header'];
  /**
   * What the rail carries when the merchant has written nothing. Serra's
   * masthead is drawn around a rail and passes the store's own description;
   * the families that never had one pass nothing and get nothing.
   */
  fallback?: string | null;
  preview?: boolean;
}) {
  const text = useShopContent();
  const [current, setCurrent] = useState(0);
  const [held, setHeld] = useState(false);
  // Nothing outside the customizer, where it is what makes the rail itself the
  // way in to the words written on it.
  const overlay = useChromeOverlay('announcement', 'Announcement bar');

  const banners: HeaderBanner[] =
    header.banners.length > 0
      ? header.banners
      : fallback
        ? [{ id: 'store-description', text: fallback, href: '', link_label: '' }]
        : [];

  // Paused in the customizer preview for the same reason the slideshow is: a
  // rail cycling under the merchant while they edit banner two is unusable.
  const rotating = header.banner_autoplay && !preview && !held && banners.length > 1;

  useEffect(() => {
    if (!rotating) return;
    const timer = window.setInterval(
      () => setCurrent((index) => (index + 1) % banners.length),
      header.banner_interval_seconds * 1000,
    );
    return () => window.clearInterval(timer);
  }, [rotating, banners.length, header.banner_interval_seconds]);

  if (banners.length === 0) return null;

  // The list can shrink under the index while a merchant deletes banners.
  const banner = banners[Math.min(current, banners.length - 1)];
  const link = safeHref(banner.href);
  const style = announcementStyle(header);

  return (
    <div
      className="relative"
      // The rail's colours, spacing and line - the theme's inverted text on
      // background unless the merchant chose their own. See `appearance.ts`.
      style={style.band}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      {/* The dots sit in the row rather than over it: a message long enough
          to reach them would otherwise be read through them. */}
      <StorefrontContainer
        className="flex min-h-[34px] items-center justify-center gap-3 py-1"
        style={style.row}
      >
        <p className="truncate text-[10px] uppercase tracking-[0.12em]" style={STOREFRONT_MONO_STYLE}>
          {banner.text}
          {banner.link_label && link ? (
            <>
              {' '}
              <MenuLink
                item={{ label: banner.link_label, url: link, items: [] }}
                preview={preview}
                className="underline underline-offset-2 hover:opacity-70"
              />
            </>
          ) : null}
        </p>

        {banners.length > 1 ? (
          // Above the customizer's selection overlay, so a merchant editing
          // banner two can still bring banner two into the rail.
          <div className="relative z-20 flex shrink-0 items-center gap-1.5">
            {banners.map((each, index) => (
              <button
                key={each.id}
                type="button"
                aria-label={text('chrome.show_announcement', { number: index + 1 })}
                aria-current={index === current}
                onClick={() => setCurrent(index)}
                className={cn(
                  'size-1.5 rounded-full bg-current transition-opacity',
                  index === current ? 'opacity-100' : 'opacity-40 hover:opacity-70',
                )}
              />
            ))}
          </div>
        ) : null}
      </StorefrontContainer>
      {overlay}
    </div>
  );
}

export interface StoreLogoProps {
  storeName: string;
  logoUrl: string | null;
  logoWidthPx: number;
  showStoreName: boolean;
  preview?: boolean;
  className?: string;
  /** How the wordmark reads - each family sets its own voice here. */
  nameClassName?: string;
  /**
   * The same, for what a class cannot say: a family whose masthead is set in
   * the theme's heading face reaches for `--storefront-heading-font` here
   * rather than hardcoding a face the merchant may have changed.
   */
  nameStyle?: CSSProperties;
  /**
   * A mark the family sets beside the wordmark - Voltic's accent full stop.
   *
   * Outside `nameClassName` because it is a second colour rather than a second
   * style: it has to sit next to the name without inheriting the name's own
   * colour. Decorative, so it stays out of the accessible label, which is the
   * store's name either way.
   */
  nameSuffix?: ReactNode;
}

/**
 * How tall the logo may get, for a merchant who asked for it to be `widthPx` wide.
 *
 * The image is capped in both directions so its own proportions survive, which
 * means the shorter cap is the one that actually decides how big it draws. A
 * height frozen at one value would therefore swallow the width setting whole:
 * anything but a very wide wordmark would sit at that height and ignore the
 * number the merchant typed. Scaling the cap with the width instead keeps the
 * control honest for every shape of logo, and the 3:1 ratio is the one the
 * defaults already drew - a 120px logo still lands at 40px tall, so no shop
 * changes appearance until someone moves the slider.
 */
function logoHeightCapPx(widthPx: number): number {
  return Math.round(widthPx / 3);
}

/**
 * The masthead: logo image, monogram fallback, and the store's name.
 *
 * A logo with the name beside it is the merchant's call, but a wordmark and no
 * name at all would leave the header unreadable to a screen reader, so the
 * name is always in the accessible label and shown whenever there is no image.
 */
export function StoreLogo({
  storeName,
  logoUrl,
  logoWidthPx,
  showStoreName,
  preview,
  className,
  nameClassName = 'text-lg font-bold tracking-tight',
  nameStyle,
  nameSuffix,
}: StoreLogoProps) {
  const paths = useStorefrontPaths();
  const overlay = useChromeOverlay('logo', 'Logo');
  const showName = showStoreName || !logoUrl;
  const content = (
    <>
      {logoUrl ? (
        <StorefrontImage
          url={logoUrl}
          sizes={fixedImageSizes(logoWidthPx)}
          priority
          placeholder={false}
          alt=""
          className="w-auto shrink-0 object-contain"
          style={{ maxWidth: `${logoWidthPx}px`, maxHeight: `${logoHeightCapPx(logoWidthPx)}px` }}
        />
      ) : (
        <span
          className="grid size-8 shrink-0 place-items-center rounded-lg text-white"
          style={{ backgroundColor: 'var(--storefront-primary)' }}
        >
          <ShoppingBag className="size-4" aria-hidden />
        </span>
      )}
      {showName ? (
        <span className={cn('truncate', nameClassName)} style={nameStyle}>
          {storeName}
          {nameSuffix ? <span aria-hidden>{nameSuffix}</span> : null}
        </span>
      ) : null}
    </>
  );

  // The mark holds its size and the name gives way, rather than the lockup
  // holding its whole width and pushing the corner of the masthead off the
  // side of a phone. A shop called something long is a shop with a long name,
  // not a shop with a broken header.
  const classes = cn('flex min-w-0 shrink items-center gap-2.5', className);
  if (preview) {
    return (
      <span aria-label={storeName} className={cn(classes, overlay && 'relative')}>
        {content}
        {overlay}
      </span>
    );
  }
  return (
    <Link to={paths.home()} aria-label={storeName} className={classes}>
      {content}
    </Link>
  );
}

/**
 * The merchant's published pages, when they have not built a footer menu.
 *
 * A shop with a returns policy and no menu pointing at it has an unreachable
 * returns policy, which is worse than not having written one. This is the
 * fallback that stops that: as soon as the merchant builds a footer menu,
 * theirs wins and this disappears.
 */
/**
 * Lower-cased link words a footer column already shows, so a second column
 * built from the shop's pages or journals can leave them out rather than list
 * "About" twice. Matched on the words a shopper reads, not the address: a menu
 * link to the about page and the page itself are the same link to them.
 */
export type ShownLinks = ReadonlySet<string>;

function notShown(label: string, shown?: ShownLinks): boolean {
  return !shown?.has(label.trim().toLowerCase());
}

/**
 * Whether the shop has a page or a journal a footer column has not already
 * listed - so a "Help" column whose every link the menu beside it already
 * carries can step aside rather than stand under its heading empty. Reads the
 * same queries `PageLinks` and `BlogLinks` do, so it costs no extra request.
 */
export function useHasUnshownHelpLinks(exclude?: ShownLinks, enabled = true): boolean {
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const features = useStorefrontFeatures();
  const pages = useQuery({ queryKey: [...scope, 'pages'], queryFn: () => source.pages(), enabled });
  const blogs = useQuery({
    queryKey: [...scope, 'blogs'],
    queryFn: () => source.blogs(),
    enabled: enabled && features.blog,
  });
  const text = useShopContent();
  const titles = [
    ...(pages.data ?? []).map((page) => page.title),
    ...(features.blog ? (blogs.data ?? []) : []).map((blog) => blog.title),
    // The Careers page, which `BlogLinks` lists beside the journals.
    ...(features.careers ? [text('careers.footer_link')] : []),
  ];
  return titles.some((title) => notShown(title, exclude));
}

export function PageLinks({ className, exclude }: { className?: string; exclude?: ShownLinks }) {
  const text = useShopContent();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const paths = useStorefrontPaths();
  const pages = useQuery({ queryKey: [...scope, 'pages'], queryFn: () => source.pages() });
  const items = (pages.data ?? []).filter((page) => notShown(page.title, exclude));
  if (items.length === 0) return null;

  return (
    <nav
      aria-label={text('chrome.nav_pages')}
      className={cn('mb-4 flex flex-wrap justify-center gap-x-5 gap-y-2', className)}
    >
      {items.map((page) => (
        <Link key={page.handle} to={paths.page(page.handle)} className="hover:opacity-70">
          {page.title}
        </Link>
      ))}
    </nav>
  );
}

/** The panel's own width, in pixels, so it can be kept on screen by hand. */
const SHELF_PANEL_WIDTH = 224;

/**
 * How long a leaving pointer has to arrive somewhere in the pair, in ms.
 *
 * The panel hangs from the bottom of the bar rather than from the shelf it
 * belongs to, so between the two there is a strip of bar that is neither: on a
 * 48px row carrying 13px links that is a good fifteen pixels of no man's land,
 * and a pointer travelling straight down through it left the shelf before it
 * reached the panel, closing the thing it was reaching for. Long enough to
 * cross the gap, short enough that a pointer that has genuinely gone somewhere
 * else does not drag the panel along behind it.
 */
const SHELF_PANEL_GRACE = 180;

/**
 * One shelf in the masthead strip, and what the merchant has filed under it.
 *
 * A shelf with nothing under it is the plain link it always was; a shelf with
 * sub-shelves opens them in a panel, because a merchant who files Headphones
 * under Audio has said where it belongs and a strip that drops that says the
 * shop is flatter than it is.
 *
 * Opens on hover for a pointer and on focus for a keyboard, and closes on
 * Escape, on leaving it, and on the navigation a followed link causes - a
 * panel left standing over the page a shopper just asked for is a panel in
 * the way. The shelf itself stays a link throughout: the sub-shelves are a
 * shortcut, never the only way through.
 */
function HeaderCategoryItem({
  branch,
  linkClassName,
  header,
}: {
  branch: ShelfBranch;
  linkClassName?: string;
  /** The merchant's header: whether the shelf opens a mega panel instead. */
  header?: StorefrontTheme['header'];
}) {
  const { category, children } = branch;
  const categoryPath = useCategoryPath();
  const location = useLocation();
  const mega = megaMenuStyle(header) !== 'off';
  const panelId = useId();
  const shelf = useRef<HTMLDivElement>(null);
  const shelfLink = useRef<HTMLAnchorElement>(null);
  // Set while Escape hands focus back to the shelf, so that focus does not
  // open the panel it has just closed.
  const quiet = useRef(false);
  const leaving = useRef<number | null>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const open = at !== null;

  /** Calls off a close the pointer has come back from - or arrived at. */
  const hold = () => {
    if (leaving.current !== null) window.clearTimeout(leaving.current);
    leaving.current = null;
  };

  const close = () => {
    hold();
    setAt(null);
  };

  // Leaving only *schedules* the close, so that the gap between the shelf and
  // the panel is crossable; entering either half calls it off again. Escape,
  // a followed link and a scrolled page still close on the spot - those are a
  // shopper who has finished with the panel rather than one crossing it.
  const leave = () => {
    hold();
    leaving.current = window.setTimeout(() => setAt(null), SHELF_PANEL_GRACE);
  };

  useEffect(() => hold, []);

  /**
   * Positioned against the viewport rather than the shelf it hangs from.
   *
   * Two of the four bars that wear this strip scroll sideways at narrow
   * widths (`overflow-x-auto`), and a box that scrolls on one axis clips the
   * other - an absolutely positioned panel inside one is a panel cut off at
   * the bar's own height. Measuring the shelf and painting the panel fixed is
   * what keeps it whole in all four, and the clamp is what keeps the last
   * shelf on a wide bar from opening off the right-hand edge.
   */
  const openAt = () => {
    hold();
    const rect = shelf.current?.getBoundingClientRect();
    if (!rect) return;
    // Hung from the bottom of the bar rather than of the shelf itself: two of
    // the bars are taller than their links, and a panel starting halfway up
    // one would cross its own bottom rule.
    const bar = shelf.current?.closest('nav')?.getBoundingClientRect();
    if (mega) {
      // A mega panel spans the page, so only its top is the shelf's business.
      setAt({ left: 0, top: bar?.bottom ?? rect.bottom });
      return;
    }
    const room = window.innerWidth - SHELF_PANEL_WIDTH - 12;
    setAt({ left: Math.max(12, Math.min(rect.left, room)), top: bar?.bottom ?? rect.bottom });
  };

  // A followed link navigates underneath an open panel, and a scrolled page
  // moves the bar out from under one - neither closes it by itself.
  useEffect(close, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (children.length === 0) {
    return (
      <Link to={categoryPath(category.slug)} className={linkClassName}>
        <CategoryLinkLabel category={category} header={header} />
      </Link>
    );
  }

  if (mega) {
    /* The mega panel: hover with a mouse, focus with a keyboard, and the
       chevron - a real button - for a finger, since a tap on a touchscreen
       sends a hover and a click together. Pointer events rather than mouse
       ones for that reason: a touch's emulated hover is skipped, so the
       button's tap is the only thing that opens it. Escape closes and hands
       focus back to the shelf. */
    return (
      <div
        ref={shelf}
        className="shrink-0"
        onPointerEnter={(event) => {
          if (event.pointerType !== 'touch') openAt();
        }}
        onPointerLeave={(event) => {
          if (event.pointerType !== 'touch') leave();
        }}
        onFocus={() => {
          if (!quiet.current) openAt();
        }}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) close();
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          close();
          quiet.current = true;
          shelfLink.current?.focus();
          quiet.current = false;
        }}
      >
        <span className="flex items-center gap-1">
          <Link ref={shelfLink} to={categoryPath(category.slug)} className={linkClassName}>
            <CategoryLinkLabel category={category} header={header} />
          </Link>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            aria-label={category.name}
            onClick={() => (open ? close() : openAt())}
            className="flex size-6 items-center justify-center"
          >
            <ChevronDown
              className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-180')}
              aria-hidden
            />
          </button>
        </span>
        {at ? (
          <CategoryMegaPanel id={panelId} branch={branch} header={header} top={at.top} />
        ) : null}
      </div>
    );
  }

  return (
    <div
      ref={shelf}
      className="shrink-0"
      onMouseEnter={openAt}
      onMouseLeave={leave}
      onFocus={openAt}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) close();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') close();
      }}
    >
      <span className="flex items-center gap-1.5">
        <Link to={categoryPath(category.slug)} className={linkClassName}>
          <CategoryLinkLabel category={category} header={header} />
        </Link>
        <ChevronDown
          className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </span>

      {at ? (
        /* Painted with the shop's own ground rather than inheriting it: this
           strip is worn on a dark bar in one family and on the page in
           another, and a panel that borrowed either would be unreadable in
           the other.

           No mouse handlers of its own, either: it is painted `fixed` but it
           is still a child of the shelf, so entering it is entering the shelf
           and the pair is one hover target however far apart they are drawn.
           A panel that listened for itself would close on the way back up to
           the shelf, which its parent reads as no departure at all. */
        <div
          className="fixed z-50 max-h-[70vh] overflow-y-auto border py-2 text-start text-sm font-normal normal-case tracking-normal shadow-xl"
          style={{
            left: at.left,
            top: at.top,
            width: SHELF_PANEL_WIDTH,
            backgroundColor: 'var(--storefront-background)',
            borderColor: 'var(--storefront-surface)',
            color: 'var(--storefront-text)',
          }}
        >
          {children.map((child) => (
            <Link
              key={child.id}
              to={categoryPath(child.slug)}
              className={cn('block truncate px-4 py-2', linkClassName)}
            >
              {child.name}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The shelves there is no room for on the strip, under one "More" at its end.
 *
 * The same card a menu's "More" opens (`MoreItem`), listing each leftover
 * shelf with its own sub-shelves indented under it - a shortcut, as every
 * panel on a bar is; the shop's whole tree is still in the menu sheet and on
 * the category pages. It scrolls rather than running off the page in a shop
 * with a great many shelves.
 */
function HeaderCategoryMore({
  branches,
  linkClassName,
}: {
  branches: ShelfBranch[];
  linkClassName?: string;
}) {
  const text = useShopContent();
  const categoryPath = useCategoryPath();
  const { open, props } = useHoverDisclosure();
  return (
    <div className="relative shrink-0" {...props}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        className={cn('flex h-full items-center gap-1', linkClassName)}
      >
        {text('header.more')}
        <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open ? (
        <div className="absolute end-0 top-full z-40 pt-2">
          <div
            className="max-h-[70vh] min-w-60 overflow-y-auto border py-2 text-start text-sm font-normal normal-case tracking-normal shadow-xl"
            style={{
              backgroundColor: 'var(--storefront-background)',
              borderColor: 'var(--storefront-surface)',
              color: 'var(--storefront-text)',
            }}
          >
            {branches.map(({ category, children }) => (
              <div key={category.id}>
                <Link to={categoryPath(category.slug)} className="block px-5 py-2 hover:opacity-60">
                  {category.name}
                </Link>
                {children.map((child) => (
                  <Link
                    key={child.id}
                    to={categoryPath(child.slug)}
                    className="block py-1.5 ps-9 pe-5 text-[14px] hover:opacity-60"
                  >
                    {child.name}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The most shelves the header strip reads: its cap and its "More" between them
 * never list more than this, as the menu sheet and `CenteredHeader` read.
 */
const HEADER_SHELF_READ = 50;

/**
 * The shop's own shelves, for the families that navigate from the masthead.
 *
 * A header full of policy pages is a filing cabinet; a shopper who lands on a
 * shop with no menu built still wants the way in to the catalogue, so this
 * stands in with the merchant's top-level categories instead.
 *
 * Top-level in the order the merchant arranged them, capped - a masthead has
 * room for a handful of links, and a shop with twenty categories would
 * otherwise push its own wordmark off centre - with each shelf's own
 * sub-shelves hanging off it in a panel. The cap counts shelves, never their
 * children: a shelf opens whole or it reads as truncated.
 *
 * `limit` is the number the *theme* was drawn with, and the merchant's own
 * `category_menu_limit` overrides it when they have set one. How many fit is a
 * question about their category names rather than about the theme - eight
 * short ones sit in a row that four long ones scroll sideways out of - so a
 * merchant who can see their own header is the one who can answer it. Passing
 * the header is what offers them the choice: a caller that leaves it out keeps
 * the theme's number whatever the merchant has chosen.
 *
 * And the strip never spills. A shop made from a theme opens with every one of
 * its industry's shelves - fourteen for a clinic - and no menu built, so this
 * strip is its whole masthead navigation, in names ("Health Checkups") that are
 * not the theme's sample ones. Like a menu bar (`FittingNav`) it shows as many
 * of the capped shelves as fit between the wordmark and the shopper's corner,
 * and the rest - those that do not fit and those past the cap - go under
 * "More", the way the theme's preview shows its menu. What is measured is the
 * invisible copy beside it (`useFittingCount`): every name in the same type,
 * the room each shelf's chevron takes, and "More" itself. The wrapper takes
 * the room it is given - `flex-1` in a row, the full width in a block - and
 * lines the strip up the way its parent lines things up, so a caller's
 * centring still holds; a caller must give it a box that is not sized by its
 * own content, or the strip would measure itself smaller each time it folded.
 *
 * Returns `null` for a shop with no categories, so a caller centring something
 * against this must keep its own placeholder.
 */
export function HeaderCategoryLinks({
  className,
  linkClassName,
  header,
  limit = 4,
}: {
  className?: string;
  linkClassName?: string;
  header?: StorefrontTheme['header'];
  limit?: number;
}) {
  const text = useShopContent();
  const all = useShelfCategoryTree({ limit: HEADER_SHELF_READ });
  const capped = all.slice(0, headerCategoryLimit(header, limit));
  const { space, measure, count } = useFittingCount(capped.length, {
    reserveMore: all.length > capped.length,
  });
  const mega = megaMenuStyle(header) !== 'off';
  if (all.length === 0) return null;

  const shown = capped.slice(0, count);
  const rest = all.slice(shown.length);
  return (
    <div ref={space} className="relative flex min-w-0 flex-1 [justify-content:inherit]">
      {/* Overflow visible: a strip that fits has nothing to scroll or clip, and
          "More" hangs its card below the bar. */}
      <nav aria-label={text('header.menu_categories')} className={cn(className, 'min-w-0 overflow-visible')}>
        {shown.map((branch) => (
          <HeaderCategoryItem
            key={branch.category.id}
            branch={branch}
            linkClassName={linkClassName}
            header={header}
          />
        ))}
        {rest.length > 0 ? <HeaderCategoryMore branches={rest} linkClassName={linkClassName} /> : null}
      </nav>
      <div
        ref={measure}
        aria-hidden
        data-shelf-measure=""
        className={cn(
          className,
          'pointer-events-none invisible absolute top-0 left-0 flex w-max overflow-visible whitespace-nowrap',
        )}
      >
        {capped.map(({ category, children }) => (
          <span
            key={category.id}
            className={cn('flex shrink-0 items-center', children.length > 0 && (mega ? 'gap-1' : 'gap-1.5'))}
          >
            {header?.category_icons ? <span className="me-2 inline-block size-6 shrink-0" /> : null}
            {category.name}
            {children.length > 0 ? <span className={mega ? 'size-6' : 'size-3.5'} /> : null}
          </span>
        ))}
        {/* A button, as the real "More" is, so it measures in the same type. */}
        <button type="button" tabIndex={-1} className={cn('flex shrink-0 items-center gap-1', linkClassName)}>
          {text('header.more')}
          <span className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * A slim row of the shop's departments under a masthead that has none of its
 * own, while the merchant has the mega menu on.
 *
 * The families that navigate by the merchant's menu alone (Classic, Bold,
 * Minimal, Editorial and any theme that inherits their header) have nowhere
 * for a department to open from, so turning the mega menu on gives them this
 * row - the same strip and the same panels every other family wears, capped by
 * the same `category_menu_limit`. Nothing at all while it is off, so a shop
 * that has not asked for it keeps exactly the header it had. Below `sm` the
 * phone's menu sheet stands in, as it does for every strip.
 */
export function MegaMenuBar({
  header,
  preview,
}: {
  header: StorefrontTheme['header'];
  preview?: boolean;
}) {
  if (preview || megaMenuStyle(header) === 'off') return null;
  return (
    <div
      className="hidden border-t sm:block preview-narrow:hidden!"
      style={{ borderColor: 'var(--storefront-surface)' }}
      data-mega-row=""
    >
      <StorefrontContainer>
        <HeaderCategoryLinks
          className={cn(
            'flex min-h-11 items-center gap-6 overflow-x-auto whitespace-nowrap text-sm font-medium',
            CHROME_QUIET_SCROLL,
          )}
          linkClassName="hover:opacity-70"
          header={header}
          limit={6}
        />
      </StorefrontContainer>
    </div>
  );
}

/**
 * The merchant's blogs, when they have not built a footer menu.
 *
 * Same fallback as {@link PageLinks}, and for the same reason: a merchant who
 * publishes a post has nothing reachable from it until they remember to add a
 * menu link, so this stands in until they build one themselves. `fetchStorefrontBlogs`
 * only ever returns blogs that already have a live post, so an empty blog a
 * merchant is still drafting never appears here.
 */
export function BlogLinks({ className, exclude }: { className?: string; exclude?: ShownLinks }) {
  const text = useShopContent();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const paths = useStorefrontPaths();
  const features = useStorefrontFeatures();
  // `enabled` rather than an early return: the shop with no blog must not ask
  // for one, and it is the request that has to stop, not just the markup.
  const blogs = useQuery({
    queryKey: [...scope, 'blogs'],
    queryFn: () => source.blogs(),
    enabled: features.blog,
  });
  const items = (features.blog ? (blogs.data ?? []) : []).filter((blog) =>
    notShown(blog.title, exclude),
  );
  // The Careers page rides along with the journals: the same kind of "more
  // about us" link, offered on the same terms - only while it is switched on,
  // and only when the merchant's own menu has not already linked it.
  const careersLabel = text('careers.footer_link');
  const careers = features.careers && notShown(careersLabel, exclude);
  if (items.length === 0 && !careers) return null;

  return (
    <nav
      aria-label={text('chrome.nav_blogs')}
      className={cn('mb-4 flex flex-wrap justify-center gap-x-5 gap-y-2', className)}
    >
      {items.map((blog) => (
        <Link key={blog.handle} to={paths.blog(blog.handle)} className="hover:opacity-70">
          {blog.title}
        </Link>
      ))}
      {careers ? (
        <Link to={paths.careers()} className="hover:opacity-70" data-careers-link>
          {careersLabel}
        </Link>
      ) : null}
    </nav>
  );
}

/**
 * The phone's way into the navigation: a hamburger, and the sheet it opens.
 *
 * Every family hides its nav row on a narrow screen - a masthead cannot carry
 * six links and a cart at 360px - which left a shopper on a phone with no way
 * into the shop's own menu at all. This is that way in, worn by whichever
 * families hide their nav and at whichever width they hide it: the caller
 * passes that width as `hiddenFrom` (`sm:hidden`, `lg:hidden`), and it is put
 * on the trigger and the sheet alike so a rotation to a wide screen cannot
 * leave a phone sheet stranded over a desktop header.
 *
 * A sheet rather than a panel dropped under the header: it is the one shape
 * that works in all five families, none of which agree on how tall their
 * header is or what is positioned inside it.
 *
 * Only a picture of the trigger in the customizer preview. The preview is a
 * narrow box on a wide page rather than a narrow viewport, so the breakpoints
 * never fire there - the device frame says when to draw it instead - and a
 * fixed sheet would cover the builder around it.
 */
interface MobileNavProps {
  menu?: { name: string; items: StorefrontMenuItem[] };
  /**
   * The width the family's own nav row appears at, as the utility that hides
   * this one from it - `sm:hidden`, `lg:hidden`. Worn by the trigger and the
   * sheet together.
   */
  hiddenFrom: string;
  /** Where the trigger sits in the family's masthead. */
  className?: string;
  preview?: boolean;
  /**
   * Whether to stand the shop's own shelves in when the merchant has built no
   * menu - the same fallback Serra's masthead makes, for the same reason: the
   * way into the catalogue is what this control is for.
   */
  categoryFallback?: boolean;
  /**
   * Whether the shop's shelves belong in the sheet *whatever* the merchant has
   * built, one level deep - department, then what is filed under it.
   *
   * For a family whose desktop bar navigates by category rather than by menu:
   * Tools & Machine opens its departments from a panel of their own, and a
   * phone that only ever saw the menu would be a phone with no way to the
   * shelves at all. Off by default, so the families whose menu *is* their
   * navigation keep the sheet they had.
   */
  categoryTree?: boolean;
}

export function MobileNav({ preview, ...props }: MobileNavProps) {
  // Gated out here rather than inside, so the preview - which renders this
  // chrome with no router around it - never reaches the router hooks below.
  // What it draws instead is the trigger alone, inert, and only in the device
  // frames narrow enough that a shopper would see one.
  if (preview) {
    const shownIn =
      props.hiddenFrom === 'lg:hidden' ? 'preview-narrow:flex!' : 'preview-phone:flex!';
    return (
      <span
        aria-hidden
        className={cn('-ms-1 hidden shrink-0 items-center p-1', shownIn, props.className)}
        style={{ color: 'var(--storefront-primary)' }}
      >
        <MenuIcon className="size-5" />
      </span>
    );
  }
  return <MobileNavSheet {...props} />;
}

function MobileNavSheet({
  menu,
  hiddenFrom,
  className,
  categoryFallback = false,
  categoryTree = false,
}: Omit<MobileNavProps, 'preview'>) {
  const [open, setOpen] = useState(false);
  const text = useShopContent();
  const location = useLocation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const categoryPath = useCategoryPath();

  const items = menu?.items ?? [];
  // The shelves come one level deep either way. `categoryTree` is about
  // *whether* they belong in the sheet at all - a family whose desktop bar
  // navigates by category needs them whatever the merchant built - and the
  // fallback only about standing them in when nothing was built; neither is a
  // reason to hide what a merchant has filed under a shelf.
  const branches = useShelfCategoryTree({
    limit: categoryTree ? 8 : 6,
    enabled: categoryTree || (categoryFallback && items.length === 0),
  });

  // A tapped link navigates underneath the sheet, so the sheet has to get out
  // of the way by itself - nothing else closes it.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);

    // The page behind a sheet must not scroll under a shopper's thumb.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [open]);

  // A shop with nothing to navigate still has a language to choose on a phone.
  const languageSwitcher = useLanguageSwitcherVisible('mobile');

  if (items.length === 0 && branches.length === 0 && !languageSwitcher) return null;

  const label =
     items.length > 0 ? (menu?.name ?? text('header.menu')) : text('header.menu_categories');

  return (
    <>
      <button
        type="button"
        aria-label={text('header.menu')}
        aria-expanded={open}
        aria-controls="storefront-mobile-nav"
        onClick={() => setOpen(true)}
        className={cn('-ms-1 flex shrink-0 items-center rounded p-1', hiddenFrom, className)}
        style={{ color: 'var(--storefront-primary)' }}
      >
        <MenuIcon className="size-5" aria-hidden />
      </button>

      {open ? (
        <div className={cn('fixed inset-0 z-50 flex', hiddenFrom)}>
          <button
            type="button"
            aria-label={text('header.menu_close')}
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/40"
          />
          <div
            id="storefront-mobile-nav"
            role="dialog"
            aria-modal="true"
            aria-label={label}
            className="relative flex h-full w-[min(20rem,85vw)] flex-col overflow-y-auto"
            style={{
              backgroundColor: 'var(--storefront-background)',
              color: 'var(--storefront-text)',
            }}
          >
            <div
              className="flex items-center justify-between border-b px-5 py-4"
              style={{ borderColor: 'var(--storefront-surface)' }}
            >
              <span className="text-xs font-semibold uppercase tracking-[0.12em]">{label}</span>
              <button
                ref={closeRef}
                type="button"
                aria-label={text('header.menu_close')}
                onClick={() => setOpen(false)}
                className="rounded p-1"
                style={{ color: 'var(--storefront-primary)' }}
              >
                <X className="size-5" aria-hidden />
              </button>
            </div>

            <nav aria-label={label} className="flex flex-col px-5 py-3 text-sm">
              {items.map((item) => (
                <div key={`${item.label}:${item.url}`} className="flex flex-col">
                  <MenuLink item={item} className="py-2.5 font-medium hover:opacity-70" />
                  {/* A submenu has nowhere to go in a masthead and is
                      dropped there; a sheet is tall, so this is the one
                      place a shopper can reach it. */}
                  {item.items.map((child) => (
                    <MenuLink
                      key={`${child.label}:${child.url}`}
                      item={child}
                      className="py-2 ps-4 opacity-70 hover:opacity-100"
                    />
                  ))}
                </div>
              ))}

              {/* The shelves, for a family that navigates by them. Under the
                  menu rather than instead of it: both are ways in, and on a
                  phone there is room to show them one after the other. */}
              {branches.length > 0 ? (
                <>
                  {items.length > 0 ? (
                    <span
                      className="mt-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.12em]"
                      style={{ color: 'var(--storefront-muted)' }}
                    >
                      {text('chrome.departments')}
                    </span>
                  ) : null}
                  {branches.map(({ category, children }) => (
                    <div key={category.id} className="flex flex-col">
                      <Link
                        to={categoryPath(category.slug)}
                        className="py-2.5 font-medium hover:opacity-70"
                      >
                        {category.name}
                      </Link>
                      {children.map((child) => (
                        <Link
                          key={child.id}
                          to={categoryPath(child.slug)}
                          className="py-2 ps-4 opacity-70 hover:opacity-100"
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  ))}
                </>
              ) : null}
            </nav>

            {languageSwitcher ? (
              <div
                className="mt-auto border-t px-5 py-4"
                style={{ borderColor: 'var(--storefront-surface)' }}
              >
                <LanguageSwitcherSlot placement="mobile" />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}


/** Menu if the merchant built one; their published pages and blogs if not. */
export function FallbackLinks({ preview }: { preview?: boolean }) {
  if (preview) return null;
  return (
    <>
      <PageLinks />
      <BlogLinks />
    </>
  );
}
